





















































































































































































































import { Component, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { domain, PaymentData, Payment, PaymentActionCode, EmailPreview } from "@/domain";
import QuickSnack from "@/components/QuickSnack.vue";
import PaymentForm from "@/components/PaymentForm.vue";
import EmailPreviewDialog from "@/components/EmailPreviewDialog.vue";
import * as util from "@/util";
import * as uiUtil from "@/uiUtil";
import { getUserTimeZone } from "@/util/dates_m";

@Component({
  components: { QuickSnack, PaymentForm, EmailPreviewDialog }
})
export default class PaymentEdit extends Vue {
  paymentID = "";
  editAgain = false;
  paymentLoading = false;
  paymentEdit = domain.backend.newPaymentData();
  payment: Payment | null = null;
  afterPublish = false;

  actionLoading: PaymentActionCode | null = null;

  showPreview = false;
  previewContent: EmailPreview | null = null;
  showLeaveConfirm = false;
  pendingNext: ((result?: false) => void) | null = null;

  get paymentFormRef(): PaymentForm | null {
    return (this.$refs.paymentForm as PaymentForm) || null;
  }

  beforeRouteLeave(_to: Route, _from: Route, next: (result?: false) => void) {
    if (this.paymentFormRef?.saving) {
      this.showLeaveConfirm = true;
      this.pendingNext = next;
    } else {
      next();
    }
  }

  cmdConfirmLeave() {
    this.showLeaveConfirm = false;
    this.pendingNext?.();
    this.pendingNext = null;
  }

  cmdCancelLeave() {
    this.showLeaveConfirm = false;
    this.pendingNext?.(false);
    this.pendingNext = null;
  }

  created() {
    if (this.$route.query.id) {
      this.paymentID = this.$route.query.id as string;
      util.promiseEnd(this.loadPayment());
    } else if (this.$route.query.copyID) {
      this.paymentID = this.$route.query.copyID as string;
      this.copyPayment();
    }
  }

  copyPayment() {
    const id = this.paymentID;
    this.paymentID = "";
    this.editAgain = false;
    this.payment = null;
    this.afterPublish = false;
    util.promiseEnd(this.loadPayment(id));
  }

  get lang() {
    return this.$store.state.lang.data;
  }

  get paymentURL(): string {
    return domain.backend.makePaymentURL(this.paymentID);
  }

  get snack(): QuickSnack {
    return this.$refs.snack as QuickSnack;
  }

  onPaymentSaved(evtData: { paymentID: string; payment: PaymentData }) {
    this.paymentID = evtData.paymentID;
    this.paymentEdit = evtData.payment;
    this.editAgain = false;
    util.promiseEnd(this.loadPayment());
  }

  onPaymentCancel() {
    this.editAgain = false;
  }

  async cmdEditAgain() {
    const success = await this.loadPayment();
    if (success) {
      this.editAgain = true;
    }
  }

  async cmdRunAction(actionName: PaymentActionCode) {
    if (!this.confirmAction(actionName)) {
      return;
    }
    try {
      this.actionLoading = actionName;
      const result = await domain.backend.paymentAction({
        key: this.paymentID,
        action: actionName
      });
      if (result.success) {
        switch (actionName) {
          case "publish":
            await this.loadPayment();
            this.snack.show(this.lang.alertPublisRequet, "success");
            this.afterPublish = true;
            break;
          case "cancel":
            await this.loadPayment();
            this.snack.show(this.lang.alertCancelRequet, "success");
            break;
          case "send_email":
            this.snack.show(this.lang.alertSuccessEmail, "success");
            break;
          case "preview_email":
            if (result.result && (result.result as EmailPreview).htmlBody) {
              this.previewContent = result.result as EmailPreview;
              this.showPreview = true;
            } else {
              this.snack.show(this.lang.errorCouldNotGeneratePreview, "error");
            }
            break;
          default:
            util.logError("Invalid action:", actionName);
            break;
        }
      } else {
        await this.loadPayment();
        this.snack.show(this.lang.failedAction, "error");
      }
    } catch (err) {
      util.logError("Error running action", {
        actionName,
        key: this.paymentID
      });
      this.snack.show(uiUtil.toErrorMsg(err, this.lang.erroCouldNotUpdateRequet), "error");
    }
    this.actionLoading = null;
  }

  get isFinishedOK() {
    return this.payment && this.payment.isFinished && this.payment.lastResult;
  }

  get status() {
    if (!this.payment || !this.payment.isFinished) {
      return null;
    }
    const result = {
      cssClass: "title text-uppercase",
      color: "warning--text",
      text: this.lang.alertFinish
    };
    if (this.payment.isExpired) {
      result.text = this.lang.alertExpired;
    } else if (this.payment.isCanceled) {
      result.text = this.lang.alertCanceled;
      result.color = "error--text";
    } else if (this.payment.lastResult && this.payment.lastResult.isApproved) {
      result.text = this.lang.alertAproved;
      result.color = "success--text";
    }
    return result;
  }

  cmdCopyURL() {
    const input = (this.$refs.paymentURL as Vue).$refs.input as HTMLInputElement;
    if (util.copyToClipboard(this.paymentURL, input)) {
      this.snack.show(this.lang.linkCopied, "success");
    }
    this.cmdSelectAll();
  }

  cmdSelectAll() {
    const input = (this.$refs.paymentURL as Vue).$refs.input as HTMLInputElement;
    if (input) {
      input.focus();
      this.$nextTick(() => {
        input.select();
        this.snack.show(this.lang.pressControlC, "");
      });
    }
  }

  confirmAction(actionName: PaymentActionCode) {
    let text = "";
    switch (actionName) {
      case "publish":
        text = this.lang.wantToPublish;
        break;
      case "cancel":
        text = this.lang.wantToCancel;
        break;
      default:
        text = "";
        break;
    }
    if (text) {
      return confirm(text);
    }
    return true;
  }

  async loadPayment(payID = "") {
    const paymentToLoad = this.paymentID || payID;
    this.paymentLoading = true;
    let success = false;

    try {
      const payment = await domain.backend.getPayment(paymentToLoad);
      if (this.paymentID) {
        this.payment = payment;
      }
      this.paymentEdit = {
        opType: payment.opType,
        createPaymentToken: true,
        customerAuthentication: payment.customerAuthentication,
        companyID: payment.companyID,
        branchID: payment.branchID,
        // User Data
        language: payment.language,
        userFullName: payment.user.fullName,
        userEmail: payment.user.email,
        changeCard: false,
        userCard: payment.user.storedCardHiddenNumber || "",
        userExpiration: "",
        // Transaction Data
        expiration: payment.expiration,
        expirationHours: payment.expirationHours || 0,
        expirationTZ: payment.expirationTZ || getUserTimeZone(),
        expirationUTC: "",
        isTest: payment.isTest,
        clientID: payment.clientID,
        amountCents: payment.amountCents,
        currency: payment.currency,
        description: payment.description || "",
        details: payment.details || [],
        attachedFiles: payment.attachedFiles || [],
        customFormFields: payment.customFormFields || [],
        notified: payment.notified,
        notifyResultURL: payment.notifyResultURL,
        notifyResultURLKo: payment.notifyResultURLKo,
        notifyEmailKo: payment.notifyEmailKo
      };
      success = true;
    } catch (err) {
      util.logError("Error loading payment", { id: paymentToLoad, err });
      this.snack.show(uiUtil.toErrorMsg(err, this.lang.couldNotLoadRequest), "error");
    }
    this.paymentLoading = false;
    return success;
  }
}
